/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { cn } from "../utils/cn";

interface FormattedMessageProps {
  content: string;
  className?: string;
}

export function FormattedMessage({ content, className }: FormattedMessageProps) {
  // Split content by code blocks first
  const parts = content.split(/(```[\s\S]*?```)/g);

  return (
    <div className={cn("space-y-3", className)}>
      {parts.map((part, index) => {
        // Check if this part is a code block
        if (part.startsWith("```") && part.endsWith("```")) {
          // Extract language and code
          const match = part.match(/```(\w+)?\n?([\s\S]*?)```/);
          const language = match?.[1] || "";
          const code = match?.[2]?.trim() || part.slice(3, -3).trim();

          return (
            <div key={index} className="my-3 rounded-xl overflow-hidden bg-zinc-900">
              {language && (
                <div className="px-4 py-2 bg-zinc-800 text-zinc-400 text-xs font-mono border-b border-zinc-700">
                  {language}
                </div>
              )}
              <pre className="p-4 overflow-x-auto">
                <code className="text-sm font-mono text-zinc-100 whitespace-pre">
                  {code}
                </code>
              </pre>
            </div>
          );
        }

        // Process inline formatting for non-code parts
        return (
          <div key={index} className="text-sm leading-relaxed">
            {formatInlineText(part)}
          </div>
        );
      })}
    </div>
  );
}

function formatInlineText(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const result: React.ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();

    // Check for markdown headers (# ## ###)
    const headerMatch = trimmedLine.match(/^(#{1,6})\s(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const content = headerMatch[2];
      const fontSizes = ["text-lg", "text-base", "text-sm", "text-sm", "text-sm", "text-sm"];
      const fontWeights = ["font-bold", "font-semibold", "font-semibold", "font-medium", "font-medium", "font-medium"];
      
      result.push(
        <div key={`${lineIndex}-header`} className={`${fontSizes[level - 1]} ${fontWeights[level - 1]} text-zinc-900 mt-4 mb-2`}>
          {processInlineFormatting(content)}
        </div>
      );
      return;
    }

    // Check for bullet points
    if (trimmedLine.startsWith("• ") || trimmedLine.startsWith("- ")) {
      result.push(
        <div key={`${lineIndex}-bullet`} className="flex items-start gap-2 my-1">
          <span className="text-pink-500 mt-1.5">•</span>
          <span>{processInlineFormatting(trimmedLine.slice(2))}</span>
        </div>
      );
      return;
    }

    // Check for numbered lists
    const numberedMatch = trimmedLine.match(/^(\d+)\.\s(.+)$/);
    if (numberedMatch) {
      result.push(
        <div key={`${lineIndex}-num`} className="flex items-start gap-2 my-1">
          <span className="text-pink-500 font-medium min-w-[1.5rem]">{numberedMatch[1]}.</span>
          <span>{processInlineFormatting(numberedMatch[2])}</span>
        </div>
      );
      return;
    }

    // Regular line
    if (trimmedLine) {
      result.push(
        <p key={lineIndex} className="my-1">
          {processInlineFormatting(line)}
        </p>
      );
    } else {
      // Empty line for spacing
      result.push(<div key={lineIndex} className="h-2" />);
    }
  });

  return result;
}

function processInlineFormatting(text: string): React.ReactNode[] {
  // Split by inline code
  const parts = text.split(/(`[^`]+`)/g);

  return parts.map((part, index) => {
    // Inline code
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={index}
          className="px-1.5 py-0.5 bg-zinc-200 rounded text-xs font-mono text-zinc-800"
        >
          {part.slice(1, -1)}
        </code>
      );
    }

    // Process bold and italic
    let processed = part;

    // Replace bold (**text**)
    const boldParts = processed.split(/(\*\*[^*]+\*\*)/g);
    if (boldParts.length > 1) {
      return boldParts.map((boldPart, boldIndex) => {
        if (boldPart.startsWith("**") && boldPart.endsWith("**")) {
          return (
            <strong key={`${index}-${boldIndex}`} className="font-semibold text-zinc-900">
              {boldPart.slice(2, -2)}
            </strong>
          );
        }
        // Process italic within non-bold parts
        return processItalic(boldPart, `${index}-${boldIndex}`);
      });
    }

    // Just italic
    return processItalic(processed, index.toString());
  });
}

function processItalic(text: string, key: string): React.ReactNode[] {
  const parts = text.split(/(\*[^*]+\*|_[^_]+_)/g);

  return parts.map((part, index) => {
    // Italic with * or _
    if ((part.startsWith("*") && part.endsWith("*")) ||
        (part.startsWith("_") && part.endsWith("_"))) {
      return (
        <em key={`${key}-${index}`} className="italic text-zinc-600">
          {part.slice(1, -1)}
        </em>
      );
    }
    return <span key={`${key}-${index}`}>{part}</span>;
  });
}
